/**
 * Application state management.
 * Simple store pattern for React state.
 */

import { useState, useCallback, useMemo } from 'react';
import type { Bridge } from '../contracts/bridge';
import type { SurfaceModelOutput } from '../contracts/modeling';
import type { RiskTier } from '../contracts/risk';
import type { MitigationPlan } from '../contracts/mitigation';

export interface AppState {
  bridges: Bridge[];
  selectedBridge: Bridge | null;
  forecast: SurfaceModelOutput[];
  currentRisk: RiskTier | null;
  mitigation: MitigationPlan | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: AppState = {
  bridges: [],
  selectedBridge: null,
  forecast: [],
  currentRisk: null,
  mitigation: null,
  isLoading: false,
  error: null,
};

export function useAppState() {
  const [state, setState] = useState<AppState>(initialState);

  const setBridges = useCallback((bridges: Bridge[]) => {
    setState((prev) => ({ ...prev, bridges }));
  }, []);

  const setSelectedBridge = useCallback((bridge: Bridge | null) => {
    setState((prev) => ({
      ...prev,
      selectedBridge: bridge,
      forecast: [],
      currentRisk: null,
      mitigation: null,
    }));
  }, []);

  const setForecast = useCallback((forecast: SurfaceModelOutput[]) => {
    setState((prev) => ({ ...prev, forecast }));
  }, []);

  const setCurrentRisk = useCallback((risk: RiskTier | null) => {
    setState((prev) => ({ ...prev, currentRisk: risk }));
  }, []);

  const setMitigation = useCallback((mitigation: MitigationPlan | null) => {
    setState((prev) => ({ ...prev, mitigation }));
  }, []);

  const setLoading = useCallback((isLoading: boolean) => {
    setState((prev) => ({ ...prev, isLoading }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState((prev) => ({ ...prev, error }));
  }, []);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  const actions = useMemo(
    () => ({
      setBridges,
      setSelectedBridge,
      setForecast,
      setCurrentRisk,
      setMitigation,
      setLoading,
      setError,
      reset,
    }),
    [
      setBridges,
      setSelectedBridge,
      setForecast,
      setCurrentRisk,
      setMitigation,
      setLoading,
      setError,
      reset,
    ]
  );

  return { state, actions };
}

export type AppActions = ReturnType<typeof useAppState>['actions'];
